import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { FileText, Plus, Eye, Edit, Trash2, Download, MessageSquare, GitBranch, ChevronRight, ChevronDown, PlusCircle } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import RFIQuestionList from "@/components/rfi/RFIQuestionList";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function RFILog({ user }) {
  const [rfis, setRfis] = useState([]);
  const [filteredRfis, setFilteredRfis] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedRfis, setExpandedRfis] = useState(new Set()); // Track expanded parent RFIs (for follow-ups)
  const [expandedProjects, setExpandedProjects] = useState(new Set()); // Track expanded projects
  
  // Filters
  const [filterProject, setFilterProject] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  
  // Dialog states
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isResponseDialogOpen, setIsResponseDialogOpen] = useState(false);
  const [selectedRfi, setSelectedRfi] = useState(null);
  
  // Form data
  const [formData, setFormData] = useState({
    project_id: "",
    rfi_number: "",
    description: "",
    item: "",
    status: "Open",
    remarks: "",
    reference_link: "",
    questions: []
  });
  
  // Project search
  const [projectSearch, setProjectSearch] = useState("");
  
  // Response form data
  const [responseText, setResponseText] = useState("");

  useEffect(() => {
    fetchRfis();
    fetchProjects();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [rfis, filterProject, filterStatus, searchTerm]);

  useEffect(() => {
    // Close dropdown when clicking outside
    const handleClickOutside = (event) => {
      const dropdown = document.getElementById('project-dropdown');
      const input = event.target.closest('input[placeholder*="Type to search"]');
      if (dropdown && !dropdown.contains(event.target) && !input) {
        dropdown.style.display = 'none';
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchRfis = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/rfis`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRfis(response.data);
    } catch (error) {
      console.error("Failed to fetch RFIs:", error);
      toast.error("Failed to load RFIs");
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/projects`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProjects(response.data);
    } catch (error) {
      console.error("Failed to fetch projects:", error);
    }
  };

  const toggleRfiExpansion = (rfiId) => {
    setExpandedRfis(prev => {
      const newSet = new Set(prev);
      if (newSet.has(rfiId)) {
        newSet.delete(rfiId);
      } else {
        newSet.add(rfiId);
      }
      return newSet;
    });
  };

  const toggleProjectExpansion = (projectId) => {
    setExpandedProjects(prev => {
      const newSet = new Set(prev);
      if (newSet.has(projectId)) {
        newSet.delete(projectId);
      } else {
        newSet.add(projectId);
      }
      return newSet;
    });
  };

  const getDisplayRfis = (rfis) => {
    // Group RFIs by project
    const projectGroups = {};
    
    rfis.forEach(rfi => {
      if (!projectGroups[rfi.project_id]) {
        projectGroups[rfi.project_id] = {
          project: {
            id: rfi.project_id,
            project_number: rfi.project_number,
            project_name: rfi.project_name
          },
          rfis: []
        };
      }
      projectGroups[rfi.project_id].rfis.push(rfi);
    });

    // Build display array with project grouping
    const result = [];
    Object.values(projectGroups).forEach(group => {
      // Add project header row
      result.push({
        type: 'project',
        project: group.project,
        count: group.rfis.length
      });

      // Add RFIs if project is expanded
      if (expandedProjects.has(group.project.id)) {
        // Get base RFIs and their follow-ups for this project
        const baseRfis = group.rfis.filter(rfi => !rfi.is_followup);
        
        baseRfis.forEach(baseRfi => {
          result.push({ type: 'rfi', data: baseRfi, level: 0 });
          
          // Find all follow-ups for this base RFI
          const followups = group.rfis.filter(rfi => rfi.parent_rfi_id === baseRfi.id);
          
          // Only show follow-ups if parent is expanded
          if (expandedRfis.has(baseRfi.id) && followups.length > 0) {
            followups.forEach(followup => {
              result.push({ type: 'rfi', data: followup, level: 1 });
            });
          }
        });
      }
    });

    return result;
  };

  const getDisplayRfisOld = (rfis) => {
    // Build a hierarchy structure
    const baseRfis = rfis.filter(rfi => !rfi.is_followup);
    const result = [];

    baseRfis.forEach(baseRfi => {
      result.push({ ...baseRfi, level: 0 });
      
      // Find all follow-ups for this base RFI
      const followups = rfis.filter(rfi => rfi.parent_rfi_id === baseRfi.id);
      
      // Only show follow-ups if parent is expanded
      if (expandedRfis.has(baseRfi.id) && followups.length > 0) {
        followups.forEach(followup => {
          result.push({ ...followup, level: 1 });
        });
      }
    });

    return result;
  };

  const applyFilters = () => {
    let filtered = [...rfis];

    // Filter by project
    if (filterProject) {
      filtered = filtered.filter(rfi => rfi.project_id === filterProject);
    }

    // Filter by status
    if (filterStatus) {
      filtered = filtered.filter(rfi => rfi.status === filterStatus);
    }

    // Search
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(rfi =>
        rfi.full_rfi_number.toLowerCase().includes(term) ||
        rfi.description.toLowerCase().includes(term) ||
        rfi.item.toLowerCase().includes(term) ||
        rfi.project_name.toLowerCase().includes(term)
      );
    }

    setFilteredRfis(filtered);
  };

  const handleCreate = async () => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/rfis`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("RFI created successfully");
      setIsCreateDialogOpen(false);
      resetForm();
      fetchRfis();
    } catch (error) {
      console.error("Failed to create RFI:", error);
      toast.error(error.response?.data?.detail || "Failed to create RFI");
    }
  };

  const handleUpdate = async () => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API}/rfis/${selectedRfi.id}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("RFI updated successfully");
      setIsEditDialogOpen(false);
      resetForm();
      fetchRfis();
    } catch (error) {
      console.error("Failed to update RFI:", error);
      toast.error(error.response?.data?.detail || "Failed to update RFI");
    }
  };

  const handleDelete = async (rfiId) => {
    if (!window.confirm("Are you sure you want to delete this RFI?")) return;

    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/rfis/${rfiId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("RFI deleted successfully");
      fetchRfis();
    } catch (error) {
      console.error("Failed to delete RFI:", error);
      toast.error(error.response?.data?.detail || "Failed to delete RFI");
    }
  };

  const handleAddResponse = async () => {
    if (!responseText.trim()) {
      toast.error("Response text cannot be empty");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API}/rfis/${selectedRfi.id}/responses`,
        { response_text: responseText },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Response added successfully");
      setResponseText("");
      setIsResponseDialogOpen(false);
      fetchRfis();
    } catch (error) {
      console.error("Failed to add response:", error);
      toast.error("Failed to add response");
    }
  };

  const handleCreateFollowup = async (rfiId) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/rfis/${rfiId}/create-followup`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Follow-up RFI created: ${response.data.followup_rfi.full_rfi_number}`);
      fetchRfis();
    } catch (error) {
      console.error("Failed to create follow-up RFI:", error);
      toast.error(error.response?.data?.detail || "Failed to create follow-up RFI");
    }
  };

  const handleQuestionsUpdate = (updatedQuestions) => {
    setFormData({ ...formData, questions: updatedQuestions });
    // Also refresh the full RFI list to get latest data
    fetchRfis();
  };

  const handleExport = async () => {
    try {
      const token = localStorage.getItem("token");
      const params = new URLSearchParams();
      if (filterProject) params.append("project_id", filterProject);
      if (filterStatus) params.append("status", filterStatus);

      const response = await axios.get(`${API}/export/rfis?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob"
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `rfis_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("RFI log exported successfully");
    } catch (error) {
      console.error("Failed to export RFIs:", error);
      toast.error("Failed to export RFI log");
    }
  };

  const openCreateDialog = () => {
    resetForm();
    setIsCreateDialogOpen(true);
  };

  const openViewDialog = (rfi) => {
    setSelectedRfi(rfi);
    setIsViewDialogOpen(true);
  };

  const openEditDialog = (rfi) => {
    setSelectedRfi(rfi);
    setFormData({
      project_id: rfi.project_id,
      rfi_number: rfi.rfi_number,
      description: rfi.description,
      item: rfi.item,
      status: rfi.status,
      remarks: rfi.remarks || "",
      reference_link: rfi.reference_link || "",
      questions: rfi.questions || []
    });
    setIsEditDialogOpen(true);
  };

  const openResponseDialog = (rfi) => {
    setSelectedRfi(rfi);
    setResponseText("");
    setIsResponseDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      project_id: "",
      rfi_number: "",
      description: "",
      item: "",
      status: "Open",
      remarks: "",
      reference_link: "",
      questions: []
    });
    setProjectSearch("");
  };

  const handleProjectChange = async (projectId) => {
    setFormData({ ...formData, project_id: projectId });
    
    // Auto-populate next RFI number for this project
    if (projectId && isCreateDialogOpen) {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(`${API}/rfis?project_id=${projectId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const existingRfis = response.data;
        const nextNumber = (existingRfis.length + 1).toString().padStart(3, '0');
        setFormData(prev => ({ ...prev, project_id: projectId, rfi_number: nextNumber }));
      } catch (error) {
        console.error("Failed to get next RFI number:", error);
        setFormData(prev => ({ ...prev, project_id: projectId, rfi_number: "001" }));
      }
    }
  };

  const getFilteredProjects = () => {
    if (!projectSearch) return projects;
    const search = projectSearch.toLowerCase();
    return projects.filter(p => 
      (p.project_number || "").toLowerCase().includes(search) ||
      (p.name || "").toLowerCase().includes(search)  // Projects use 'name' field
    );
  };

  const getStatusColor = (status) => {
    const colors = {
      "Open": "bg-blue-100 text-blue-800",
      "Partially Closed": "bg-yellow-100 text-yellow-800",
      "Closed": "bg-green-100 text-green-800",
      "Follow-up": "bg-purple-100 text-purple-800"
    };
    return colors[status] || "bg-md3-surface-container text-md3-on-surface";
  };

  const canCreateOrEdit = () => {
    const allowedLevels = ["Team Lead", "Manager", "Admin"];
    return (user?.department === "Projects" && allowedLevels.includes(user?.access_level)) ||
           user?.department === "Management";
  };

  const canDelete = () => {
    return (user?.department === "Projects" && user?.access_level === "Admin") ||
           user?.department === "Management";
  };

  return (
    <Layout user={user}>
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-md3-display-small flex items-center gap-2">
              <FileText className="h-8 w-8 text-blue-600" />
              RFI Log
            </h1>
            <p className="text-md3-on-surface-variant mt-1">Request for Information - Project Management</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleExport} variant="outline" className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full">
              <Download className="h-4 w-4 mr-2" />
              Export Excel
            </Button>
            {canCreateOrEdit() && (
              <Button className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full" onClick={openCreateDialog}>
                <Plus className="h-4 w-4 mr-2" />
                New RFI
              </Button>
            )}
          </div>
        </div>

        {/* Filters */}
        <Card className="mb-6 shadow-md3-1 hover:shadow-md3-2 transition-shadow duration-200 ">
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <Label>Project</Label>
                <Select value={filterProject || "all"} onValueChange={(value) => setFilterProject(value === "all" ? "" : value)}>
                  <SelectTrigger className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20">
                    <SelectValue placeholder="All Projects" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Projects</SelectItem>
                    {projects.map(project => (
                      <SelectItem key={project.id} value={project.id}>
                        {project.project_number} - {project.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Status</Label>
                <Select value={filterStatus || "all"} onValueChange={(value) => setFilterStatus(value === "all" ? "" : value)}>
                  <SelectTrigger className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20">
                    <SelectValue placeholder="All Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="Open">Open</SelectItem>
                    <SelectItem value="Partially Closed">Partially Closed</SelectItem>
                    <SelectItem value="Closed">Closed</SelectItem>
                    <SelectItem value="Follow-up">Follow-up</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <Label>Search</Label>
                <div className="flex gap-2">
                  <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" placeholder="Search by RFI#, description, item, or project..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <Button variant="outline" className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full"
                    onClick={() => {
                      setFilterProject("");
                      setFilterStatus("");
                      setSearchTerm("");
                    }}
                    title="Clear all filters"
                  >
                    Clear
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* RFI Table */}
        <Card className="bg-md3-surface-container shadow-md3-1 hover:shadow-md3-2 transition-shadow duration-200 rounded-md3-lg border-0">
          <CardHeader>
            <CardTitle className="text-md3-title-large text-md3-on-surface ">
              RFIs ({filteredRfis.filter(r => !r.is_followup).length} base, {filteredRfis.filter(r => r.is_followup).length} follow-ups)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">Loading...</div>
            ) : filteredRfis.length === 0 ? (
              <div className="text-center py-8 text-md3-on-surface-variant">No RFIs found</div>
            ) : (
              <div className="overflow-x-auto">
                <Table className="bg-md3-surface-container rounded-md3-md">
                  <TableHeader>
                    <TableRow>
                      <TableHead>RFI #</TableHead>
                      <TableHead>Project</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Item</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Questions</TableHead>
                      <TableHead>Responses</TableHead>
                      <TableHead>Created By</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {getDisplayRfis(filteredRfis).map((row, index) => {
                      if (row.type === 'project') {
                        const isExpanded = expandedProjects.has(row.project.id);
                        return (
                          <TableRow key={`project-${row.project.id}`} className="bg-md3-surface-container hover:bg-gray-200 font-semibold">
                            <TableCell colSpan={9}>
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => toggleProjectExpansion(row.project.id)}
                                  className="hover:bg-gray-300 rounded p-0.5"
                                >
                                  {isExpanded ? (
                                    <ChevronDown className="h-4 w-4 text-md3-on-surface-variant" />
                                  ) : (
                                    <ChevronRight className="h-4 w-4 text-md3-on-surface-variant" />
                                  )}
                                </button>
                                <span className="text-blue-900">
                                  {row.project.project_number} - {row.project.project_name}
                                </span>
                                <span className="text-md3-on-surface-variant text-md3-body-small font-normal">
                                  ({row.count} RFI{row.count !== 1 ? 's' : ''})
                                </span>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      } else {
                        const rfi = row.data;
                        const hasFollowups = filteredRfis.some(f => f.parent_rfi_id === rfi.id);
                        const isExpanded = expandedRfis.has(rfi.id);
                        const indentPx = 12 + (row.level * 24); // Base indent + follow-up indent
                        
                        return (
                          <TableRow key={rfi.id} className={row.level === 1 ? "bg-md3-surface" : ""}>
                          <TableCell className="font-mono font-semibold">
                            <div 
                              className="flex items-center gap-2"
                              style={{ paddingLeft: `${indentPx}px` }}
                            >
                              {/* Expand/Collapse button for base RFIs with follow-ups */}
                              {row.level === 0 && hasFollowups && (
                                <button
                                  onClick={() => toggleRfiExpansion(rfi.id)}
                                  className="hover:bg-gray-200 rounded p-0.5"
                                >
                                  {isExpanded ? (
                                    <ChevronDown className="h-4 w-4 text-md3-on-surface-variant" />
                                  ) : (
                                    <ChevronRight className="h-4 w-4 text-md3-on-surface-variant" />
                                  )}
                                </button>
                              )}
                              
                              {/* Spacer for base RFIs without follow-ups */}
                              {row.level === 0 && !hasFollowups && (
                                <div className="w-5" />
                              )}
                              
                              {/* Follow-up indicator icon */}
                              {rfi.is_followup && (
                                <GitBranch className="h-4 w-4 text-purple-500" title="Follow-up RFI" />
                              )}
                              
                              {/* RFI Number */}
                              <span>{rfi.full_rfi_number}</span>
                            </div>
                            {rfi.parent_rfi_number && (
                              <div 
                                className="text-xs text-md3-on-surface-variant mt-1"
                                style={{ paddingLeft: `${indentPx + 20}px` }}
                              >
                                ↳ from {rfi.parent_rfi_number}
                              </div>
                            )}
                          </TableCell>
                        <TableCell>
                          <div className="text-md3-body-small">
                            <div className="font-medium">{rfi.project_number}</div>
                            <div className="text-md3-on-surface-variant">{rfi.project_name}</div>
                          </div>
                        </TableCell>
                        <TableCell className="max-w-xs truncate">
                          {rfi.description}
                        </TableCell>
                        <TableCell className="max-w-xs truncate">
                          {rfi.item}
                        </TableCell>
                        <TableCell>
                          <Badge className={getStatusColor(rfi.status)}>
                            {rfi.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <span className="text-md3-body-small font-medium">{rfi.questions?.length || 0}</span>
                            <span className="text-xs text-md3-on-surface-variant\">Q</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <MessageSquare className="h-4 w-4 text-gray-400" />
                            <span>{rfi.responses?.length || 0}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-md3-body-small text-md3-on-surface-variant">
                          {rfi.created_by_name}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button variant="ghost" className="hover:bg-md3-surface-container-high rounded-md3-full transition-all duration-200"
                              size="sm"
                              onClick={() => openViewDialog(rfi)}
                              title="View RFI"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" className="hover:bg-md3-surface-container-high rounded-md3-full transition-all duration-200"
                              size="sm"
                              onClick={() => openResponseDialog(rfi)}
                              title="Add Response"
                            >
                              <MessageSquare className="h-4 w-4" />
                            </Button>
                            {canCreateOrEdit() && (
                              <Button variant="outline" className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full"
                                size="sm"
                                onClick={() => openEditDialog(rfi)}
                                title="Edit RFI"
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                            )}
                            {/* Show Create Follow-up button for base RFIs with "Follow-up" status */}
                            {canCreateOrEdit() && !rfi.is_followup && rfi.status === "Follow-up" && (
                              <Button variant="ghost" className="hover:bg-md3-surface-container-high rounded-md3-full transition-all duration-200 text-purple-600 hover:text-purple-700"
                                size="sm"
                                onClick={() => handleCreateFollowup(rfi.id)}
                                title="Create Follow-up RFI"
                              >
                                <PlusCircle className="h-4 w-4" />
                              </Button>
                            )}
                            {canDelete() && (
                              <Button variant="destructive" className="bg-red-600 hover:bg-red-700 text-white shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full"
                                size="sm"
                                onClick={() => handleDelete(rfi.id)}
                                title="Delete RFI"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                        );
                      }
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Create/Edit Dialog */}
        <Dialog open={isCreateDialogOpen || isEditDialogOpen} onOpenChange={(open) => {
          setIsCreateDialogOpen(false);
          setIsEditDialogOpen(false);
          if (!open) resetForm();
        }}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white">
            <DialogHeader>
              <DialogTitle className="text-md3-headline-small text-md3-on-surface font-semibold ">
                {isCreateDialogOpen ? "Create New RFI" : "Edit RFI"}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Project *</Label>
                  {isEditDialogOpen ? (
                    <Input className="bg-md3-surface-container focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20" value={`${projects.find(p => p.id === formData.project_id)?.project_number || ""} - ${projects.find(p => p.id === formData.project_id)?.name || ""}`}
                      disabled
                    />
                  ) : (
                    <div className="relative">
                      <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" placeholder="Type to search or select from dropdown..."
                        value={projectSearch}
                        onChange={(e) => {
                          setProjectSearch(e.target.value);
                          // Show dropdown when typing
                          const dropdown = document.getElementById('project-dropdown');
                          if (dropdown) dropdown.style.display = 'block';
                        }}
                        onFocus={() => {
                          const dropdown = document.getElementById('project-dropdown');
                          if (dropdown) dropdown.style.display = 'block';
                        }}
                      />
                      {/* Dropdown list */}
                      <div
                        id="project-dropdown"
                        className="absolute z-50 w-full mt-1 bg-md3-surface-container border border-md3-outline rounded-md3-md shadow-lg max-h-60 overflow-auto hidden"
                        style={{ display: 'none' }}
                      >
                        {getFilteredProjects().length > 0 ? (
                          getFilteredProjects().map(project => (
                            <div
                              key={project.id}
                              className="px-3 py-2 hover:bg-blue-50 cursor-pointer border-b border-gray-100"
                              onClick={() => {
                                handleProjectChange(project.id);
                                setProjectSearch(`${project.project_number || ''} - ${project.name || ''}`);
                                document.getElementById('project-dropdown').style.display = 'none';
                              }}
                            >
                              <div className="font-semibold text-blue-900">{project.project_number || 'N/A'}</div>
                              <div className="text-md3-body-small text-md3-on-surface-variant">{project.name || 'Unnamed Project'}</div>
                            </div>
                          ))
                        ) : (
                          <div className="px-3 py-2 text-md3-on-surface-variant text-md3-body-small">No projects found</div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <div>
                  <Label>RFI # *</Label>
                  <Input className={`bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 ${isEditDialogOpen ? "bg-md3-surface-container" : ""}`} value={formData.rfi_number}
                    onChange={(e) => setFormData({ ...formData, rfi_number: e.target.value })}
                    placeholder="Auto-populated (e.g., 001)"
                    disabled={isEditDialogOpen}
                  />
                  <p className="text-xs text-md3-on-surface-variant mt-1">
                    Auto-generated, but you can edit before creating
                  </p>
                </div>
              </div>
              <div>
                <Label>Description *</Label>
                <Textarea className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe the request for information"
                  rows={3}
                />
              </div>
              <div>
                <Label>Item *</Label>
                <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" value={formData.item}
                  onChange={(e) => setFormData({ ...formData, item: e.target.value })}
                  placeholder="Item or component related to RFI"
                />
              </div>
              <div>
                <Label>Status *</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value) => setFormData({ ...formData, status: value })}
                >
                  <SelectTrigger className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Open">Open</SelectItem>
                    <SelectItem value="Partially Closed">Partially Closed</SelectItem>
                    <SelectItem value="Closed">Closed</SelectItem>
                    <SelectItem value="Follow-up">Follow-up</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Remarks</Label>
                <Textarea className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" value={formData.remarks}
                  onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                  placeholder="Additional remarks or notes"
                  rows={2}
                />
              </div>
              <div>
                <Label>Reference Link</Label>
                <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" value={formData.reference_link}
                  onChange={(e) => setFormData({ ...formData, reference_link: e.target.value })}
                  placeholder="https://... (hyperlink to reference file)"
                />
              </div>

              {/* Questions Section - Only in Edit Mode */}
              {isEditDialogOpen && selectedRfi && (
                <div className="border-t pt-4 mt-4">
                  <RFIQuestionList
                    rfiId={selectedRfi.id}
                    questions={formData.questions}
                    onQuestionsUpdate={handleQuestionsUpdate}
                    isEditing={true}
                  />
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full"
                onClick={() => {
                  setIsCreateDialogOpen(false);
                  setIsEditDialogOpen(false);
                  resetForm();
                }}
              >
                Cancel
              </Button>
              <Button className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full" onClick={isCreateDialogOpen ? handleCreate : handleUpdate}>
                {isCreateDialogOpen ? "Create RFI" : "Update RFI"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* View Dialog */}
        <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-white">
            <DialogHeader>
              <DialogTitle className="flex items-center justify-between text-md3-headline-small text-md3-on-surface font-semibold">
                <div className="flex items-center gap-2">
                  {selectedRfi?.is_followup && (
                    <GitBranch className="h-5 w-5 text-purple-500" />
                  )}
                  RFI Details - {selectedRfi?.full_rfi_number}
                </div>
                {selectedRfi && !selectedRfi.is_followup && canCreateOrEdit() && (
                  <Button size="sm"
                    variant="outline"
                    className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full" onClick={() => handleCreateFollowup(selectedRfi.id)}>
                    <GitBranch className="h-4 w-4 mr-2" />
                    Create Follow-up
                  </Button>
                )}
              </DialogTitle>
            </DialogHeader>
            {selectedRfi && (
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-md3-on-surface-variant ">Project</Label>
                    <p className="font-medium">
                      {selectedRfi.project_number} - {selectedRfi.project_name}
                    </p>
                  </div>
                  <div>
                    <Label className="text-md3-on-surface-variant ">Status</Label>
                    <div>
                      <Badge className={getStatusColor(selectedRfi.status)}>
                        {selectedRfi.status}
                      </Badge>
                    </div>
                  </div>
                </div>
                {selectedRfi.parent_rfi_number && (
                  <div className="bg-purple-50 border border-purple-200 rounded-md3-lg p-3">
                    <Label className="text-md3-on-surface-variant ">Parent RFI</Label>
                    <p className="font-mono text-md3-body-small mt-1">{selectedRfi.parent_rfi_number}</p>
                  </div>
                )}
                <div>
                  <Label className="text-md3-on-surface-variant ">Description</Label>
                  <p className="mt-1">{selectedRfi.description}</p>
                </div>
                <div>
                  <Label className="text-md3-on-surface-variant ">Item</Label>
                  <p className="mt-1">{selectedRfi.item}</p>
                </div>
                {selectedRfi.remarks && (
                  <div>
                    <Label className="text-md3-on-surface-variant ">Remarks</Label>
                    <p className="mt-1">{selectedRfi.remarks}</p>
                  </div>
                )}
                {selectedRfi.reference_link && (
                  <div>
                    <Label className="text-md3-on-surface-variant ">Reference Link</Label>
                    <a
                      href={selectedRfi.reference_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline block mt-1"
                    >
                      {selectedRfi.reference_link}
                    </a>
                  </div>
                )}

                {/* Questions Section */}
                {selectedRfi.questions && selectedRfi.questions.length > 0 && (
                  <div className="border-t pt-4">
                    <RFIQuestionList
                      rfiId={selectedRfi.id}
                      questions={selectedRfi.questions}
                      onQuestionsUpdate={() => fetchRfis()}
                      isEditing={false}
                    />
                  </div>
                )}

                <div>
                  <Label className="text-md3-on-surface-variant mb-2 block ">
                    Responses ({selectedRfi.responses?.length || 0})
                  </Label>
                  {selectedRfi.responses && selectedRfi.responses.length > 0 ? (
                    <div className="space-y-3 bg-md3-surface p-4 rounded-md3-lg max-h-60 overflow-y-auto">
                      {selectedRfi.responses.map((response, index) => (
                        <div key={response.id} className="bg-md3-surface-container p-3 rounded border">
                          <p className="text-md3-body-small mb-2">{response.response_text}</p>
                          <div className="flex justify-between text-xs text-md3-on-surface-variant">
                            <span>{response.added_by_name}</span>
                            <span>{new Date(response.added_at).toLocaleString()}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-md3-on-surface-variant text-md3-body-small">No responses yet</p>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-4 text-md3-body-small text-md3-on-surface-variant">
                  <div>
                    <Label className="text-md3-on-surface-variant ">Created By</Label>
                    <p>{selectedRfi.created_by_name}</p>
                  </div>
                  <div>
                    <Label className="text-md3-on-surface-variant ">Created At</Label>
                    <p>{new Date(selectedRfi.created_at).toLocaleString()}</p>
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Add Response Dialog */}
        <Dialog open={isResponseDialogOpen} onOpenChange={setIsResponseDialogOpen}>
          <DialogContent className="bg-white">
            <DialogHeader>
              <DialogTitle className="text-md3-headline-small text-md3-on-surface font-semibold ">Add Response</DialogTitle>
              <DialogDescription className="text-md3-body-medium text-gray-600">
                Add a response to RFI: {selectedRfi?.full_rfi_number}
              </DialogDescription>
            </DialogHeader>
            <div>
              <Label>Response Text *</Label>
              <Textarea className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" value={responseText}
                onChange={(e) => setResponseText(e.target.value)}
                placeholder="Enter your response..."
                rows={4}
              />
            </div>
            <DialogFooter>
              <Button variant="outline" className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full" onClick={() => setIsResponseDialogOpen(false)}>
                Cancel
              </Button>
              <Button className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full" onClick={handleAddResponse}>Add Response</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
